import { PayloadAction } from "@reduxjs/toolkit";
import cartItemsAdapter from "@/redux/slice/cart/adapter/cartItem";
import { CartState } from "@/redux/slice/cart/types/CartState";
import { CartItem } from "@/redux/slice/cart/types/CartItem";
import { CartInputDataV2 } from "@/components/graphql/types/globalTypes";
import { ThunkResolvedStatus } from "@/libs/enum/ThunkResolvedStatus";
import { CartLocation } from "@/redux/slice/cart/types/CartLocation";

type UpdateCartItemQtyPayload = {
  sku: string;
  quantity: number;
};

const syncCartActionReducers = {
  updateCartItemQuantity(state: CartState, action: PayloadAction<UpdateCartItemQtyPayload>) {
    const { sku, quantity } = action?.payload;
    cartItemsAdapter.updateOne(state.cartItems, {
      id: sku,
      changes: {
        quantity,
      },
    });
  },
  addUpdateCartItem(state, action: PayloadAction<CartItem>) {
    const cartItem = action?.payload;
    cartItemsAdapter.upsertOne(state.cartItems, cartItem);
  },
  setAllowFetch(state: CartState, action: PayloadAction<boolean>) {
    state.allowFetch = action.payload;
  },
  addUpdateCartItems(state: CartState, action: PayloadAction<CartItem[]>) {
    const updateCartItems = action?.payload;
    cartItemsAdapter.upsertMany(state.cartItems, updateCartItems);
  },
  setCartItems(state: CartState, action: PayloadAction<CartItem[]>) {
    cartItemsAdapter.setAll(state.cartItems, action?.payload);
  },
  setUserActionPayload(state: CartState, action: PayloadAction<Partial<CartInputDataV2>>) {
    state.userActionPayload = action?.payload;
  },
  setGuestUserActionPayload(state: CartState, action: PayloadAction<Partial<CartInputDataV2>>) {
    state.guestUserActionPayload = action?.payload;
  },
  setIsCartUpdated(state: CartState, action: PayloadAction<boolean>) {
    state.isCartUpdated = action?.payload;
  },
  setFetchingCart(state: CartState, action: PayloadAction<ThunkResolvedStatus>) {
    state.fetchingCart = action?.payload;
  },
  setCartFetchingUpdating(state: CartState, action: PayloadAction<ThunkResolvedStatus>) {
    state.cartFetchingUpdating = action?.payload;
  },
  setUpdatingCart(state: CartState, action: PayloadAction<ThunkResolvedStatus>) {
    state.updatingCart = action?.payload;
  },
  setServerCartErrorMessage(state: CartState, action: PayloadAction<string>) {
    state.serverCartErrorMessage = action?.payload;
  },

  setOOSNotified(state: CartState, action: PayloadAction<string>) {
    const sku = action?.payload;
    cartItemsAdapter.updateOne(state.cartItems, {
      id: sku,
      changes: {
        oosNotified: true,
      },
    });
  },

  setCartLocation(state: CartState, action: PayloadAction<CartLocation>) {
    state.cartLocation = action?.payload;
  },
  setAllowServerUpdate(state: CartState, action: PayloadAction<boolean>) {
    state.allowServerUpdate = action?.payload;
  },

  appendOutOfStockSkus(state: CartState, action: PayloadAction<string[]>) {
    const skuSet = new Set(state.outOfStockSkuList);
    const payloadList = action?.payload;

    if (payloadList?.length) {
      payloadList.forEach((payloadSkuId) => {
        skuSet.add(payloadSkuId);
      });
    }

    state.outOfStockSkuList = Array.from(skuSet);
  },
  markSkuListInStock(state: CartState, action: PayloadAction<string[]>) {
    const skuSet = new Set(state.outOfStockSkuList);
    const payloadList = action?.payload;

    if (payloadList?.length) {
      payloadList.forEach((payloadSkuId) => {
        if (skuSet.has(payloadSkuId)) {
          skuSet.delete(payloadSkuId);
        }
      });
    }

    state.outOfStockSkuList = Array.from(skuSet);
  },
  setCartTotalAmount(state: CartState, action: PayloadAction<number>) {
    state.cartTotalAmount = action?.payload;
  },
};

export default syncCartActionReducers;
